import {
  authDescriptionSlate,
  authHeadingSlate,
  authTopBarBorder,
  borderInput,
  paletteErrorMain,
  palettePrimaryMain,
  authCardSurfaceSx,
  surfaceAuthCard,
} from '@/theme/designTokens'

/** Figma 5074-11995 — Main Verification Card */
export const FORGOT_PASSWORD_CARD_WIDTH_PX = 480
export const FORGOT_PASSWORD_CARD_HEIGHT_PX = 641
/** Figma 5074-12053 — sent card padding (matches left/right inset) */
export const FORGOT_PASSWORD_CARD_PADDING_PX = 49
/** Figma 5074-12053 — gap between card sections (logo / title / buttons) */
export const FORGOT_PASSWORD_SENT_CARD_SECTION_GAP_PX = 19
/** Figma 5074-12057 — Password Reset + two-line subtitle */
export const FORGOT_PASSWORD_SENT_TITLE_BLOCK_HEIGHT_PX = 93
/** Figma 5074-12060 — subtitle container */
export const FORGOT_PASSWORD_SENT_SUBTITLE_MIN_HEIGHT_PX = 40
/** Figma 5074-12057 — heading frame (y=0) to subtitle container (y=53) */
export const FORGOT_PASSWORD_SENT_HEADING_TO_SUBTITLE_GAP_PX = 8
export const FORGOT_PASSWORD_LOGO_BLOCK_HEIGHT_PX = 88
export const FORGOT_PASSWORD_TITLE_BLOCK_HEIGHT_PX = 73
/** Figma 5074-12028 — heading frame around 5074-12029 */
export const FORGOT_PASSWORD_HEADING_FRAME_HEIGHT_PX = 45
/** Figma 5074-12027 — gap between heading frame (y=45) and subtitle (y=53) */
export const FORGOT_PASSWORD_HEADING_TO_SUBTITLE_GAP_PX = 8
export const FORGOT_PASSWORD_FORM_BLOCK_HEIGHT_PX = 228
export const FORGOT_PASSWORD_FORM_ERROR_ROW_HEIGHT_PX = 20
export const FORGOT_PASSWORD_EMAIL_FIELD_BLOCK_HEIGHT_PX = 78
export const FORGOT_PASSWORD_LABEL_TO_INPUT_GAP_PX = 8.39
export const FORGOT_PASSWORD_INPUT_HEIGHT_PX = 50
export const FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX = 1
export const FORGOT_PASSWORD_OUTLINE_BUTTON_BORDER_WIDTH_PX = 1.5
export const FORGOT_PASSWORD_CARD_FOOTER_SPACER_PX = 32

/** Figma B.Error / 5074-12040 */
export const FORGOT_PASSWORD_FIELD_ERROR_COLOR = paletteErrorMain

/** Figma 5074-12040 — server / not-found copy */
export const FORGOT_PW_EMAIL_NOT_FOUND_MSG = 'Invalid: Email not found'

/** Figma 5074-11993 / 5074-12021 — verification card */
const forgotPasswordCardBaseSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: FORGOT_PASSWORD_CARD_WIDTH_PX,
  border: `1px solid ${authTopBarBorder}`,
  borderRadius: '12px',
  boxShadow: '0px 4px 2px rgba(0, 0, 0, 0.25)',
  p: `${FORGOT_PASSWORD_CARD_PADDING_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: '19px',
  alignItems: 'stretch',
  boxSizing: 'border-box',
  overflow: 'visible',
} as const

/** Figma 5074-11995 — request form card */
export const forgotPasswordCardSx = {
  ...forgotPasswordCardBaseSx,
  minHeight: FORGOT_PASSWORD_CARD_HEIGHT_PX,
} as const

/** Figma 5074-12053 — email sent confirmation card (height from content; 49px padding) */
export const forgotPasswordSentCardSx = {
  ...forgotPasswordCardBaseSx,
  gap: `${FORGOT_PASSWORD_SENT_CARD_SECTION_GAP_PX}px`,
  height: 'auto',
  minHeight: 'unset',
} as const

/** Figma 5074-12057 — sent confirmation title block (content-driven; no fixed height). */
export const forgotPasswordSentTitleBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${FORGOT_PASSWORD_SENT_HEADING_TO_SUBTITLE_GAP_PX}px`,
  flexShrink: 0,
  width: '100%',
} as const

/** Sent card heading — auto height so multi-line title does not overlap subtitle. */
export const forgotPasswordSentHeadingFrameSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

/** Figma 5074-12060 — subtitle container around 5074-12061 */
export const forgotPasswordSentSubtitleContainerSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const forgotPasswordLogoBlockSx = {
  height: FORGOT_PASSWORD_LOGO_BLOCK_HEIGHT_PX,
  minHeight: FORGOT_PASSWORD_LOGO_BLOCK_HEIGHT_PX,
  maxHeight: FORGOT_PASSWORD_LOGO_BLOCK_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

/** Figma 5074-12027 — Forget Password (5074-12029) + subtitle (5074-12031). */
export const forgotPasswordTitleBlockSx = {
  minHeight: FORGOT_PASSWORD_TITLE_BLOCK_HEIGHT_PX,
  height: FORGOT_PASSWORD_TITLE_BLOCK_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${FORGOT_PASSWORD_HEADING_TO_SUBTITLE_GAP_PX}px`,
  flexShrink: 0,
  width: '100%',
} as const

export const forgotPasswordHeadingFrameSx = {
  minHeight: FORGOT_PASSWORD_HEADING_FRAME_HEIGHT_PX,
  height: FORGOT_PASSWORD_HEADING_FRAME_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const forgotPasswordFormBlockSx = {
  minHeight: FORGOT_PASSWORD_FORM_BLOCK_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
} as const

/** Figma 5074-12033 — label (5074-12035) + input (5074-12036/12037). */
export const forgotPasswordEmailLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.9525px',
  color: authHeadingSlate,
  display: 'block',
  m: 0,
  p: 0,
  minHeight: 20,
} as const

export const forgotPasswordEmailFieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${FORGOT_PASSWORD_LABEL_TO_INPUT_GAP_PX}px`,
  minHeight: FORGOT_PASSWORD_EMAIL_FIELD_BLOCK_HEIGHT_PX,
  flexShrink: 0,
} as const

export const forgotPasswordEmailErrorRowSx = {
  minHeight: FORGOT_PASSWORD_FORM_ERROR_ROW_HEIGHT_PX,
  display: 'flex',
  alignItems: 'flex-start',
  pl: '5px',
  flexShrink: 0,
} as const

/** Figma 5074-12029 — Header2_32_bold */
export const forgotPasswordHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', md: '32px' },
  lineHeight: { xs: '32px', md: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  minHeight: 32,
  width: '100%',
} as const

/** Figma 5074-12031 — 14px / 20px secondary description */
export const forgotPasswordSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: 0,
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  minHeight: 20,
  width: '100%',
} as const

/** Figma 5074-12060 / 5074-12061 — confirmation copy (wraps for long emails). */
export const forgotPasswordSentSubtitleSx = {
  ...forgotPasswordSubtitleSx,
  letterSpacing: '-0.1504px',
  wordBreak: 'break-word',
} as const

export const forgotPasswordPrimaryButtonSx = {
  bgcolor: palettePrimaryMain,
  borderRadius: '8px',
  minHeight: 56,
  px: '24px',
  py: '16px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  color: '#ffffff',
  '&:hover': {
    bgcolor: '#007bb8',
  },
  '&.Mui-disabled': {
    bgcolor: '#94a3b8',
    color: '#ffffff',
  },
} as const

/** Figma 5074-12042 — outlined Back to Login (1.5px #008CD0, 56px, 8px radius). */
export const forgotPasswordOutlineButtonSx = {
  borderRadius: '8px',
  minHeight: 56,
  height: 56,
  px: '24px',
  py: '16px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '24px',
  letterSpacing: 0,
  textTransform: 'none',
  color: palettePrimaryMain,
  bgcolor: surfaceAuthCard,
  borderWidth: `${FORGOT_PASSWORD_OUTLINE_BUTTON_BORDER_WIDTH_PX}px`,
  borderStyle: 'solid',
  borderColor: palettePrimaryMain,
  '&.MuiButton-outlined': {
    borderWidth: `${FORGOT_PASSWORD_OUTLINE_BUTTON_BORDER_WIDTH_PX}px`,
  },
  '&:hover': {
    bgcolor: '#e6f5fc',
    borderWidth: `${FORGOT_PASSWORD_OUTLINE_BUTTON_BORDER_WIDTH_PX}px`,
    borderColor: palettePrimaryMain,
  },
} as const

/** Figma 5074-12040 — error copy */
export const forgotPasswordFieldErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: FORGOT_PASSWORD_FIELD_ERROR_COLOR,
  m: 0,
} as const

/** Figma 5074-12037 — email field (50px, 1px #EDEDED, 16×14 padding). */
export const forgotPasswordInputSx = {
  height: FORGOT_PASSWORD_INPUT_HEIGHT_PX,
  minHeight: FORGOT_PASSWORD_INPUT_HEIGHT_PX,
  bgcolor: surfaceAuthCard,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '14px',
    fontSize: '14px',
    lineHeight: 'normal',
    letterSpacing: '-0.9525px',
    color: '#04183f',
    '&::placeholder': {
      fontSize: '14px',
      color: '#9ca3af',
      opacity: 1,
    },
  },
} as const

/** Figma 5074-12037 error state — 1px B.Error border (not MUI default 2px). */
export const forgotPasswordErrorInputSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: FORGOT_PASSWORD_FIELD_ERROR_COLOR,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: FORGOT_PASSWORD_FIELD_ERROR_COLOR,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: FORGOT_PASSWORD_FIELD_ERROR_COLOR,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: FORGOT_PASSWORD_FIELD_ERROR_COLOR,
    borderWidth: `${FORGOT_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
} as const
